import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiService } from '../services/api';
import './NotificationCenter.css';
import Button from './ui/Button';
import SegmentedControl from './ui/SegmentedControl';
import NotificationSettings from './NotificationSettings';
 

interface Notification {
  _id: string;
  title: string;
  message: string;
  type: 'invitation' | 'meal_plan_update' | 'shopping_list_update' | 'chore_assigned' | 'chore_completed' | 'recipe_shared' | 'general';
  read: boolean;
  createdAt: string;
  timeAgo: string;
  sender?: {
    name: string;
    email: string;
    avatar?: string;
  };
  family?: {
    name: string;
  };
  data?: any;
}

interface NotificationCenterProps {
  isOpen: boolean;
  onClose: () => void;
}

const NotificationCenter: React.FC<NotificationCenterProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState<'all' | 'unread' | 'settings'>('all');

  useEffect(() => {
    if (isOpen && activeTab !== 'settings') {
      loadNotifications();
    }
  }, [isOpen, activeTab]);

  // Toggle body scroll lock when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('notification-overlay-open');
    } else {
      document.body.classList.remove('notification-overlay-open');
    }

    return () => {
      document.body.classList.remove('notification-overlay-open');
    };
  }, [isOpen]);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      const response = await apiService.notifications.getAll({
        unreadOnly: activeTab === 'unread',
        page: 1,
        limit: 50,
      });

      if (response.data?.data) {
        setNotifications(response.data.data.notifications);
        setUnreadCount(response.data.data.unreadCount);
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId: string) => {
    try {
      await apiService.notifications.markAsRead(notificationId);
      setNotifications(prev => 
        prev.map(notification => 
          notification._id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await apiService.notifications.markAllAsRead();
      setNotifications(prev => prev.map(notification => ({ ...notification, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const deleteNotification = async (notificationId: string) => {
    try {
      await apiService.notifications.delete(notificationId);
      setNotifications(prev => prev.filter(notification => notification._id !== notificationId));
      if (!notifications.find(n => n._id === notificationId)?.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const getNotificationIcon = (type: Notification['type']) => {
    switch (type) {
      case 'invitation':
        return '👥';
      case 'meal_plan_update':
        return '📅';
      case 'shopping_list_update':
        return '🛒';
      case 'chore_assigned':
        return '✅';
      case 'chore_completed':
        return '🎉';
      case 'recipe_shared':
        return '📖';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type: Notification['type']) => {
    switch (type) {
      case 'invitation':
        return 'notification-invitation';
      case 'meal_plan_update':
        return 'notification-meal-plan';
      case 'shopping_list_update':
        return 'notification-shopping';
      case 'chore_assigned':
        return 'notification-chore-assigned';
      case 'chore_completed':
        return 'notification-chore-completed';
      case 'recipe_shared':
        return 'notification-recipe';
      default:
        return 'notification-general';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="notification-overlay" onClick={onClose}>
      <div className="notification-center" onClick={(e) => e.stopPropagation()}>
        <div className="notification-header">
          <h3>{t('notifications:title')}</h3>
          <button className="close-button" onClick={onClose}>
            ✕
          </button>
        </div>

        <SegmentedControl<'all' | 'unread' | 'settings'>
          size="sm"
          ariaLabel="Notification filter"
          options={[
            { label: `${t('notifications:tabs.all')} (${notifications.length})`, value: 'all' },
            { label: `${t('notifications:tabs.unread')} (${unreadCount})`, value: 'unread' },
            { label: t('notifications:tabs.settings'), value: 'settings' },
          ]}
          value={activeTab}
          onChange={(val) => setActiveTab(val)}
        />

        {activeTab === 'settings' ? (
          <div className="notification-settings-tab">
            <NotificationSettings />
          </div>
        ) : (
          <>
            {unreadCount > 0 && (
              <div className="notification-actions">
                <Button size="sm" variant="primary" onClick={markAllAsRead}>{t('notifications:actions.markAllAsRead')}</Button>
              </div>
            )}

            <div className="notification-list">
              {loading ? (
                <div className="loading-notifications">
                  <div className="spinner"></div>
                  <p>{t('notifications:messages.loading')}</p>
                </div>
              ) : notifications.length === 0 ? (
                <div className="empty-notifications">
                  <div className="empty-icon">🔔</div>
                  <p>{t('notifications:messages.noNotifications')}</p>
                  <small>{t('notifications:messages.noNotificationsDescription')}</small>
                </div>
              ) : (
                notifications.map((notification) => (
                  <div
                    key={notification._id}
                    className={`notification-item ${notification.read ? 'read' : 'unread'} ${getNotificationColor(notification.type)}`}
                    onClick={() => !notification.read && markAsRead(notification._id)}
                  >
                    <div className="notification-icon">
                      {getNotificationIcon(notification.type)}
                    </div>
                    <div className="notification-content">
                      <div className="notification-header">
                        <h4 className="notification-title">{notification.title}</h4>
                        <span className="notification-time">{notification.timeAgo}</span>
                      </div>
                      <p className="notification-message">{notification.message}</p>
                      {notification.sender && (
                        <div className="notification-sender">
                          {t('notifications:from')}: {notification.sender.name}
                        </div>
                      )}
                    </div>
                    <div className="notification-actions">
                      {!notification.read && (
                        <div className="unread-indicator"></div>
                      )}
                      <button
                        className="delete-notification-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteNotification(notification._id);
                        }}
                        title={t('notifications:actions.delete')}
                      >
                        🗑️
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default NotificationCenter; 